import { useInitialPublicData } from '../../seo/PublicBootstrap.jsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Briefcase,
  Building2,
  CalendarDays,
  Cat,
  Car,
  ChevronLeft,
  ChevronRight,
  Dumbbell,
  Gift,
  GraduationCap,
  HeartPulse,
  Home,
  Map,
  Moon,
  Palette,
  ShoppingBag,
  Sparkles,
  Ticket,
  Utensils,
} from 'lucide-react';
import { Button, PublicContainer } from '@mts/ui';
import { usePublicTenant } from '../../hooks/usePublicTenant';
import { fetchCategories } from '../../services/api';

const iconMap = {
  palette: Palette,
  car: Car,
  sparkles: Sparkles,
  briefcase: Briefcase,
  'academic-cap': GraduationCap,
  ticket: Ticket,
  heart: Dumbbell,
  utensils: Utensils,
  'plus-circle': HeartPulse,
  home: Home,
  star: Gift,
  moon: Moon,
  gift: Gift,
  paw: Cat,
  'shopping-bag': ShoppingBag,
  map: Map,
  'building-library': Building2,
};

function hexToRgba(hex, alpha, fallback) {
  if (typeof hex !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(hex)) {
    return fallback;
  }

  const red = parseInt(hex.slice(1, 3), 16);
  const green = parseInt(hex.slice(3, 5), 16);
  const blue = parseInt(hex.slice(5, 7), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function QuickCategoryItem({ category, onClick, fallbackColor, tintColor }) {
  const Icon = iconMap[category.icon] || CalendarDays;
  const tint = hexToRgba(
    category.color || tintColor,
    0.12,
    hexToRgba(fallbackColor, 0.08, 'rgba(15, 43, 91, 0.08)')
  );

  return (
    <button
      type="button"
      onClick={() => onClick?.(category)}
      className="group flex min-w-[96px] flex-col items-center gap-3 rounded-2xl px-3 py-3 text-center transition-transform duration-200 hover:-translate-y-0.5 snap-center flex-shrink-0"
    >
      <div
        className="flex h-14 w-14 items-center justify-center rounded-full transition-transform duration-200 group-hover:scale-105"
        style={{ backgroundColor: tint, color: category.color || tintColor }}
      >
        <Icon size={24} strokeWidth={2} />
      </div>
      <span
        className="font-directory text-sm font-semibold leading-tight transition-colors duration-200"
        style={{ color: 'var(--primary)' }}
        onMouseEnter={(event) => {
          event.currentTarget.style.color = 'var(--primary-hover)';
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.color = 'var(--primary)';
        }}
      >
        {category.name}
      </span>
    </button>
  );
}

function CategoryGrid({ onCategoryClick }) {
  const { brandTheme } = usePublicTenant();
  const fallbackColor = brandTheme?.muted || '#6B7280';
  const tintColor = brandTheme?.primary || '#1D4ED8';
  const initial = useInitialPublicData("categories");
  const [categories, setCategories] = useState(initial || []);
  const [loading, setLoading] = useState(!initial);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (initial) { setCategories(initial); setLoading(false); return; }
    fetchCategories()
      .then((data) => setCategories(data))
      .catch(() => setCategories([]))
      .finally(() => setLoading(false));
  }, [initial]);

  function normalizeCategory(cat) {
    return {
      id: cat.id,
      name: cat.name,
      description: cat.description || cat.coreCategory?.description || '',
      slug: cat.slug,
      color: cat.coreCategory?.color || fallbackColor,
      icon: cat.coreCategory?.icon || '',
    };
  }

  const topLevelVisible = useMemo(() => {
    return categories
      .filter((c) => !c.parentId && c.isVisible !== false)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map(normalizeCategory);
  }, [categories]);

  const handleCategoryClick = (category) => {
    if (onCategoryClick) {
      onCategoryClick(category);
    }
  };

  const updateScrollState = () => {
    const container = scrollContainerRef.current;
    if (!container) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    setCanScrollLeft(container.scrollLeft > 4);
    setCanScrollRight(maxScrollLeft - container.scrollLeft > 4);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || loading) return undefined;

    updateScrollState();

    const handleResize = () => updateScrollState();
    container.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', handleResize);
    };
  }, [loading, topLevelVisible.length]);

  const handleScrollLeft = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.scrollBy({
      left: -320,
      behavior: 'smooth',
    });
  };

  const handleScrollRight = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.scrollBy({
      left: 320,
      behavior: 'smooth',
    });
  };

  return (
    <section className="relative z-10 -mt-6 bg-transparent pb-10">
      <style>{`.category-quick-nav::-webkit-scrollbar{display:none;}`}</style>
      <PublicContainer>
        <div className="rounded-[24px] bg-white px-4 py-5 shadow-[0_18px_50px_rgba(15,23,42,0.10)] ring-1 ring-slate-200/80 lg:px-6">
          {loading ? (
          <div className="flex gap-2 overflow-hidden">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex min-w-[96px] flex-col items-center gap-3 px-3 py-3">
                <div className="h-14 w-14 animate-pulse rounded-full bg-slate-100" />
                <div className="h-4 w-20 animate-pulse rounded bg-slate-100" />
              </div>
            ))}
          </div>
        ) : topLevelVisible.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-500">No categories available.</p>
        ) : (
          <div className="flex items-center gap-2">
            {canScrollLeft ? (
              <Button
                variant="secondary"
                aria-label="Scroll categories left"
                onClick={handleScrollLeft}
                className="hidden h-10 w-10 shrink-0 rounded-full border-slate-200 p-0 text-slate-700 shadow-sm lg:inline-flex"
              >
                <ChevronLeft size={18} />
              </Button>
            ) : null}

            <div
              ref={scrollContainerRef}
              className="category-quick-nav flex flex-1 gap-1 overflow-x-auto snap-x snap-mandatory scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {topLevelVisible.map((category) => (
                <QuickCategoryItem
                  key={category.id}
                  category={category}
                  onClick={handleCategoryClick}
                  fallbackColor={fallbackColor}
                  tintColor={tintColor}
                />
              ))}
            </div>

            {canScrollRight ? (
              <Button
                variant="secondary"
                aria-label="Scroll categories right"
                onClick={handleScrollRight}
                className="hidden h-10 w-10 shrink-0 rounded-full border-slate-200 p-0 text-slate-700 shadow-sm lg:inline-flex"
              >
                <ChevronRight size={18} />
              </Button>
            ) : null}
          </div>
        )}
        </div>
      </PublicContainer>
    </section>
  );
}

export default CategoryGrid;
